import { EstateLevel } from "@1games-ru/region-shared/dist/enums/estate/estate";
import React from "react";
import { useTranslation } from "react-i18next";
import HotelCdn from "@/core/cdn/hotel/hotel-cdn";
import formatPrice from "@/core/helpers/formatPrice";
import LockedIcon from "@/shared/widgets/LockedIcon";
import HotelCkeckbox from "@/domains/hotel/components/HotelCkeckbox";

type Props = {
  isLock: boolean;
  src: string;
  uuid: string;
  level: EstateLevel;
  price: number;
  list: {
    residence?: boolean;
    garageCapacity?: number;
    bed?: boolean;
    fridge?: boolean;
    foodDelivery?: boolean;
    intercom?: boolean;
    safe?: boolean;
    wardrobe?: boolean;
    guestRoom?: boolean;
    balcony?: boolean;
  };
};

export default function HotelRoomControlView({
  isLock,
  src,
  uuid,
  level,
  price,
  list = {},
}: Props) {
  const { t } = useTranslation("hotel");

  const bgImage = uuid && src ? `url(${HotelCdn.getHotelImage(uuid, src)})` : "";

  const leftCol = ["residence", "garageCapacity", "bed", "fridge", "foodDelivery"];
  const rightCol = ["intercom", "safe", "wardrobe", "guestRoom", "balcony"];

  return (
    <div className="relative w-full h-[21.875rem] mb-2.5 rounded-2xl overflow-clip">
      <div
        className="absolute top-0 left-0 w-full h-45"
        style={{
          backgroundImage: bgImage,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute bottom-0 left-0 w-full h-28 bg-gradient-to-t from-dark-900 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-dark-900 to-transparent" />
        <LockedIcon isLock={isLock} className="absolute top-2.5 left-2.5" />
        <div className="absolute bottom-5 left-0 flex justify-between items-center w-full px-6">
          <p className="text-18 font-bold leading-none">{t(`hotel.level.${level}`)}</p>
          <p className="text-16 font-bold leading-none">{formatPrice(price)} ₽</p>
        </div>
      </div>
      <div className="absolute left-0 bottom-0 flex w-full">
        <div className="flex flex-col gap-1.5 w-1/2 pl-5 pr-3 pb-5">
          {Object.entries(list).map((item) => {
            if (leftCol.includes(item[0]) && item[0] === "garageCapacity") {
              return (
                <React.Fragment key={item[0]}>
                  <HotelCkeckbox
                    id={item[0]}
                    checked
                    text={t(`hotel.${item[0]}`)}
                    isSecond={false}
                    disabled
                  />
                  <HotelCkeckbox
                    id="secondParking"
                    checked={Number(item[1]) > 1}
                    text={t("hotel.secondParking")}
                    isSecond
                    disabled
                  />
                </React.Fragment>
              );
            }
            if (leftCol.includes(item[0])) {
              return (
                <HotelCkeckbox
                  key={item[0]}
                  id={item[0]}
                  checked={!!item[1]}
                  text={t(`hotel.${item[0]}`)}
                  isSecond={item[0] === "foodDelivery"}
                  disabled
                />
              );
            }
            return null;
          })}
        </div>
        <div className="flex flex-col gap-1.5 w-1/2 px-5 pb-5">
          {Object.entries(list).map((item) => {
            if (rightCol.includes(item[0])) {
              let text = item[0];
              if (text === "safe" && level === EstateLevel.Luxury) text = "safeLuxury";
              return (
                <HotelCkeckbox
                  key={text}
                  id={text}
                  checked={!!item[1]}
                  text={t(`hotel.${text}`)}
                  isSecond={false}
                  disabled
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
}
